import {CHAT_PERSONAS, ChatPersonaType} from "../../toktiv-common/types";
import {ChatMessage, MessageFactory, ParticipantKey, ParticipantType} from "../shared/messages/chat/types";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../../shared/BaseFragment";
import React, {ReactElement} from "react";
import {App} from "../App";
import {ChatAgent} from "./ChatAgent";
import {colorRed, colorYellow, lightGray, translucentWhite} from "../../shared/colors";
import {StyledBoxColumn, StyledBoxRow, StyledDurationView} from "../../shared/StyledComponents";
import {BORDER_RADIUS_LARGE, PAGE_FRAGMENT_SM_WIDTH, PD_LG, PD_MD, PD_SM, PD_XLG, SZ_LG} from "../../shared/dimens";
import {Button, CircularProgress, IconButton, Typography} from "@mui/material";
import {CallEndOutlined} from "@mui/icons-material";
import {AvatarImage} from "../util/Components";
import {getMemberAuth} from "../../shared/auth";
import {UserDisplayName, UserProfilePhoto} from "../../shared/types";
import {RecordAudioDisplayMode, RecordAudioFragment} from "../../shared/RecordAudioFragment";
import {ClockGranularity} from "../../shared/clock";
import {AppPrefs} from "../app_prefs";

export type AppAudioCallFragmentProps = BaseFragmentProps & {
  personaType: ChatPersonaType,
  myParticipantKey: ParticipantKey,
  onBackPressed: () => boolean,
}

export type AppAudioCallFragmentState = BaseFragmentState & {
  agentBusy?: boolean,
  agentText?: string,
  audioStatus?: ReactElement,
  audioPlaying?: boolean,
  callStartedAt: number,
  messages: ChatMessage[],
}

export class AppAudioCallFragment extends BaseFragment<AppAudioCallFragmentProps, AppAudioCallFragmentState> {

  private readonly memberAuth = getMemberAuth();
  private readonly audio = App.getAppRefs().audio;
  private readonly agent: ChatAgent = new ChatAgent(this.props.myParticipantKey);
  private readonly appPrefs = AppPrefs.getInstance();

  protected onCreateState(): AppAudioCallFragmentState {
    return {
      ...super.onCreateState(),
      callStartedAt: Date.now(),
      messages: [],
    };
  }

  componentDidUpdate(prevProps: Readonly<AppAudioCallFragmentProps>, prevState: Readonly<AppAudioCallFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.audioPlaying !== this.state.audioPlaying) {
      if (prevState.audioPlaying && !this.state.audioPlaying) {
        this.setState({
          agentBusy: false,
        });
      }
      this.setState({
        audioStatus: this.state.audioPlaying
          ? <StyledBoxRow style={{padding: PD_MD, alignItems: "center", justifyContent: "center"}}>
            <Typography>Speaking...</Typography>
            <Button
              variant="contained"
              onClick={() => this.audio.pause()}>
              Stop
            </Button>
          </StyledBoxRow>
          : null,
      })
    }
  }

  private async playText(text: string) {
    const encodedFile = await this.agent.textToSpeech(this.props.personaType, text);
    this.audio.src = "data:audio/mp3;base64," + encodedFile;
    this.audio.play();
  }

  componentDidMount() {
    super.componentDidMount();
    this.audio.addEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.audio.removeEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  onPlayStateChanged(): void {
    this.setState({
      audioPlaying: !this.audio.paused,
    });
  }

  private async respond(url: string): Promise<void> {
    this.setState({
      agentBusy: true,
      agentText: null,
    });
    const text = await this.agent.speechToText(url);
    let messages = [...this.state.messages, MessageFactory.createTextMessage(null, this.props.myParticipantKey, text)];
    const response = await this.agent.chat(this.props.personaType, messages);
    await this.playText(response);
    messages = [...messages, MessageFactory.createTextMessage(null, new ParticipantKey(this.props.personaType, ParticipantType.MEMBER), text)];
    this.setState({
      agentText: response,
      messages: messages,
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    const persona = CHAT_PERSONAS.find(persona => persona.type === this.props.personaType);
    const profilePhotoSize = SZ_LG;
    return <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: PD_LG,
        backgroundImage: "linear-gradient(315deg, #eec0c6 0%, #7ee8fa 74%)",
        // background: nightGray,
      }}>
      <StyledBoxColumn style={{padding: PD_LG, flexGrow: 1}}>
        <StyledDurationView startedAt={this.state.callStartedAt} granularity={ClockGranularity.SECOND}
                            renderDuration={durationMs => {
                              let t = Math.floor(durationMs / 1000);
                              const sec = t % 60;
                              const min = Math.floor(t /= 60) % 60;
                              const hour = Math.floor(t /= 60);
                              return (hour > 0 ? (hour + ":") : "") + min.toString().padStart(2, "0") + ":" + sec.toString().padStart(2, "0");
                            }}/>
        <StyledBoxColumn style={{
          gap: PD_XLG,
          flexBasis: 0,
          flexGrow: 1,
          padding: PD_LG,
          alignItems: "center",
        }}>
          <StyledBoxColumn style={{
            filter: !this.state.agentBusy ? "grayscale(1)" : null,
            opacity: !this.state.agentBusy ? 0.5 : null,
          }}>
            <div style={{
              boxSizing: "content-box",
              background: lightGray,
              width: profilePhotoSize,
              borderRadius: BORDER_RADIUS_LARGE,
              border: "6px solid " + (this.state.agentBusy ? colorYellow : null),
              aspectRatio: 1
            }}>
              <AvatarImage
                key={persona.type}
                persona={persona}
                flip={true}
                style={{width: profilePhotoSize}}/>
            </div>
          </StyledBoxColumn>
          <Typography variant="h6">{persona.displayName}</Typography>
          {!this.state.agentBusy || this.state.agentText
            ? (this.state.agentText
                ? <StyledBoxColumn style={{
                  width: "100%",
                  maxWidth: PAGE_FRAGMENT_SM_WIDTH,
                  background: translucentWhite,
                  borderRadius: BORDER_RADIUS_LARGE,
                  padding: PD_LG,
                  margin: "auto",
                  flexGrow: 1,
                  overflowY: "scroll",
                  height: 0
                }}>
                  <Typography>
                    {this.state.agentText}
                  </Typography>
                </StyledBoxColumn>
                : <StyledBoxRow style={{alignItems: "center", justifyContent: "center"}}>
                  <Typography>Listening...</Typography>
                </StyledBoxRow>
            )
            : <StyledBoxRow style={{alignItems: "center", justifyContent: "center"}}>
              <CircularProgress/>
              <Typography>Thinking...</Typography>
            </StyledBoxRow>}
        </StyledBoxColumn>
        <StyledBoxColumn style={{
          gap: PD_XLG,
          padding: PD_LG,
          alignItems: "center",
          pointerEvents: this.state.agentBusy ? "none" : "auto",
          filter: this.state.agentBusy ? "grayscale(1)" : null,
          opacity: this.state.agentBusy ? 0.5 : null,
        }}>
          <div style={{
            boxSizing: "content-box",
            width: profilePhotoSize,
            borderRadius: BORDER_RADIUS_LARGE,
            border: "6px solid " + (!this.state.agentBusy ? colorYellow : null),
            aspectRatio: 1
          }}>
            <img src={UserProfilePhoto(this.memberAuth.member.user)}
                 style={{width: "100%", height: "100%", objectFit: "cover", borderRadius: BORDER_RADIUS_LARGE - 8}}/>
          </div>
          <Typography variant="h6">{UserDisplayName(this.memberAuth.member.user)}</Typography>
          <RecordAudioFragment
            disabled={this.state.agentBusy}
            autoStart={this.appPrefs.getAutoswitchCallTurn()}
            autoStop={this.appPrefs.getAutoswitchCallTurn()}
            displayMode={RecordAudioDisplayMode.EMBED}
            onDone={(url: string) => {
              this.respond(url);
            }}/>
        </StyledBoxColumn>
      </StyledBoxColumn>
      <StyledBoxRow style={{padding: PD_LG, alignItems: "center", justifyContent: "flex-end"}}>
        <IconButton
          onClick={() => {
            this.props.onBackPressed();
          }}
          style={{background: colorRed, color: "white"}}>
          <CallEndOutlined style={{width: 48, height: 48, padding: PD_SM}}/>
        </IconButton>
      </StyledBoxRow>
    </div>;
  }
}